import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ProjectListItem } from '@greco-gls/shared';
import { Link } from 'react-router';

import { helpAnchor } from '@/help/anchor';

import {
  COVERAGE_STEPS,
  coverageStep,
  formatCount,
  localeColumns,
  percentOf,
  targetUnits,
  type Units,
} from './coverage';

const PROJECT_WIDTH = 240;
const OVERALL_WIDTH = 84;
const CELL_WIDTH = 68;

/**
 * One row per project, one column per locale, each cell the share of that locale already written
 * (spec §9.3).
 *
 * A cell is a link to the project's Keys screen filtered to what is missing in that locale, so the
 * moment someone sees "de-AT 62" is also the moment they can start on it. The project and overall
 * columns stay put while the locales scroll sideways; thirty projects carry more locales than any
 * screen is wide.
 */
export function CoverageMatrix({
  items,
  onOpenProject,
}: {
  items: ProjectListItem[];
  onOpenProject: (code: string) => void;
}) {
  const locales = localeColumns(items);

  return (
    <Box sx={{ overflowX: 'auto' }} data-help="dashboard-coverage">
      <Table sx={{ borderCollapse: 'separate', borderSpacing: 0, width: 'auto', minWidth: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...stickyAt(0, PROJECT_WIDTH), fontWeight: 600 }}>Project</TableCell>
            <TableCell
              align="center"
              sx={{ ...stickyAt(PROJECT_WIDTH, OVERALL_WIDTH), fontWeight: 600, ...edge }}
            >
              Translated
            </TableCell>
            {locales.map((locale) => (
              <TableCell
                key={locale}
                align="center"
                sx={{ width: CELL_WIDTH, minWidth: CELL_WIDTH, px: 0.5, whiteSpace: 'nowrap' }}
              >
                <Typography sx={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 600 }}>
                  {locale}
                </Typography>
              </TableCell>
            ))}
            {/* Takes up the slack when a few locales leave the table narrower than the card. */}
            <TableCell sx={{ width: '100%', p: 0 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const { code, name, sourceLocale } = item.project;
            const keysPath = `/projects/${code}/keys`;
            const open = () => onOpenProject(code);
            const byLocale = new Map(item.coverage.map((entry) => [entry.locale, entry]));
            const overall = targetUnits(item);

            return (
              <TableRow key={code} hover>
                <TableCell sx={{ ...stickyAt(0, PROJECT_WIDTH), py: 0.75 }}>
                  <Typography
                    component={Link}
                    to={keysPath}
                    onClick={open}
                    data-help={helpAnchor('dashboard-project-link')}
                    noWrap
                    sx={{
                      display: 'block',
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: 'primary.main',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {name}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>
                    <Box component="span" sx={{ fontFamily: 'monospace' }}>
                      {code}
                    </Box>{' '}
                    · {formatCount(item.keyCount)} keys
                    {item.roles.length ? (
                      <Box component="span" data-help={helpAnchor('dashboard-project-roles')}>
                        {' '}
                        · {item.roles.join(', ')}
                      </Box>
                    ) : null}
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ ...stickyAt(PROJECT_WIDTH, OVERALL_WIDTH), p: '2px', ...edge }}
                >
                  <CoverageCell
                    to={keysPath}
                    onClick={open}
                    units={overall}
                    description={`${name}, all target locales`}
                    emphasis
                  />
                </TableCell>
                {locales.map((locale) => {
                  const entry = byLocale.get(locale);
                  return (
                    <TableCell key={locale} align="center" sx={{ p: '2px' }}>
                      {entry ? (
                        <CoverageCell
                          // The percentage counts the project's package keys too, so the list
                          // behind it has to show them or a package gap reads as "No keys".
                          to={`${keysPath}?missingIn=${encodeURIComponent(locale)}&includePackages=1`}
                          onClick={open}
                          units={entry}
                          description={`${code} · ${locale}`}
                          source={locale === sourceLocale}
                        />
                      ) : (
                        <Typography
                          aria-label={`${code} does not use ${locale}`}
                          sx={{ fontSize: 12, color: 'text.disabled' }}
                        >
                          ·
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell sx={{ p: 0 }} />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function CoverageCell({
  to,
  onClick,
  units,
  description,
  source = false,
  emphasis = false,
}: {
  to: string;
  onClick: () => void;
  units: Units;
  description: string;
  source?: boolean;
  emphasis?: boolean;
}) {
  const percent = percentOf(units);
  if (percent === null) {
    return <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>—</Typography>;
  }

  const step = coverageStep(percent);
  const missing = units.total - units.translated;
  const summary =
    missing === 0
      ? `all ${formatCount(units.total)} values written`
      : `${formatCount(units.translated)} of ${formatCount(units.total)} written, ${formatCount(missing)} missing`;
  const title = source
    ? `${description} (source locale): ${summary}. A key without a source text is never exported.`
    : `${description}: ${summary}.`;

  return (
    <Tooltip title={title} placement="top" disableInteractive>
      <Box
        component={Link}
        to={to}
        onClick={onClick}
        aria-label={title}
        data-help={source ? undefined : helpAnchor('dashboard-coverage-cell')}
        sx={{
          display: 'block',
          py: emphasis ? 0.75 : 0.5,
          borderRadius: '4px',
          // The source column is not on the scale: its coverage is "has a source text".
          bgcolor: (theme) =>
            source ? theme.palette.coverageSource : theme.palette.coverage[step.level]?.fill,
          color: source ? 'text.secondary' : (theme) => theme.palette.coverage[step.level]?.ink,
          border: source ? '1px dashed' : 'none',
          borderColor: 'text.disabled',
          fontSize: emphasis ? 13.5 : 12.5,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          textDecoration: 'none',
          outlineOffset: 1,
          '&:hover, &:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        {percent} %
      </Box>
    </Tooltip>
  );
}

/** The scale, read left to right as the cells get darker. */
export function CoverageLegend() {
  return (
    <Box
      data-help="dashboard-legend"
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}
    >
      {COVERAGE_STEPS.map((step) => (
        <Swatch key={step.min} level={step.level} label={step.label} />
      ))}
      <Swatch label="source locale" dashed />
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>· not used</Typography>
    </Box>
  );
}

/** One step of the scale, or the source column's fill when `level` is absent. */
function Swatch({ level, label, dashed = false }: { level?: number; label: string; dashed?: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: '3px',
          bgcolor: (theme) =>
            level === undefined ? theme.palette.coverageSource : theme.palette.coverage[level]?.fill,
          border: dashed ? '1px dashed' : 'none',
          borderColor: 'text.disabled',
        }}
      />
      <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  );
}

const stickyAt = (left: number, width: number) => ({
  position: 'sticky' as const,
  left,
  zIndex: 1,
  width,
  minWidth: width,
  maxWidth: width,
  bgcolor: 'background.paper',
});

/** The line between the columns that stay and the locales that scroll under them. */
const edge = { borderRight: 1, borderRightColor: 'divider' };
